/**
 * Реализация `api`: канонические эндпоинты.
 *
 * Здесь живут вход и «кто вошёл» (`/auth/csrf`, `/auth/login`, `/auth/logout`,
 * `/auth/me`) и РЕЕСТРЫ читателей именованных наборов. Сами читатели лежат в
 * `api/<домен>.ts`: каждый срез переноса приносит свой файл и ровно одну
 * строку в список ниже, поэтому параллельные ветки не встречаются в одном
 * куске кода.
 *
 * Реестра три, и разница между ними — это разница ВОПРОСОВ, а не стилей.
 *
 * `ROUTE_ADAPTERS` спрашиваются ПЕРВЫМИ и отвечают только на своих маршрутах.
 * Кабинет хозяйства читает имена наборов, ОБЩИЕ с экранами района (`meta.json`,
 * `cycles.json`, `works-<цикл>.json`), но со своим смыслом: «мои работы», а не
 * «работы района». Выбрать между ними по имени нечем — выбирает маршрут.
 *
 * `ADAPTERS` берутся за набор по ИМЕНИ: имя принадлежит одному домену и
 * значит на всех экранах одно и то же. `READERS` — то же самое для доменов,
 * которые отдают готовый `ReadResult` вместе с причиной отказа.
 *
 * Набор, за который не взялся никто, получает ОТКАЗ СЛОВАМИ. Пустой ответ
 * вместо отказа означал бы «данных нет» там, где на самом деле «канон этого
 * пока не отдаёт», и экран напечатал бы ноль вместо «нет данных».
 */

import { api, type PrincipalOut, type QueryParams } from '../../api/client';
import type { DatasetAdapter } from './api/adapter';
import { refusal, setNotBuilt } from './api/adapter';
import { cabinetAdapter } from './api/cabinet';
import { cardsAdapter } from './api/cards';
import type { RouteAdapter } from './api/corpus';
import { cyclesAdapter } from './api/cycles';
import type { DatasetReader } from './api/dataset';
import { obligationsAdapter } from './api/obligations';
import { quarantineAdapter } from './api/quarantine';
import { readRegistryDataset } from './api/registry';
import { seasonAdapter } from './api/season';
import { templatesAdapter } from './api/templates';
import { entryAdapter, verificationAdapter } from './api/verification';
import { worksAdapter } from './api/works';
import { notConnected } from './refusals';
import type { DataSource, ReadResult, SourcePrincipal } from './types';


/** Адаптеры, привязанные к маршрутам. Одна строка на домен экранов. */
const ROUTE_ADAPTERS: readonly RouteAdapter[] = [
  cabinetAdapter, // кабинет хозяйства — срез 1.7
  verificationAdapter, // очередь проверки — /verification/queue
  entryAdapter, // ввод за хозяйство — /my/works
];

/** Адаптеры канона по имени набора. Одна строка на домен — по одной на срез. */
const ADAPTERS: readonly DatasetAdapter[] = [
  cyclesAdapter,
  worksAdapter,
  templatesAdapter,
  obligationsAdapter,
  seasonAdapter,
  cardsAdapter,
  quarantineAdapter, // карантин района — /quarantine/statuses
];

/** Читатели наборов. Порядок значения не имеет: имена не пересекаются. */
const READERS: readonly DatasetReader[] = [readRegistryDataset];

function toSourcePrincipal(value: PrincipalOut): SourcePrincipal {
  return {
    is_authenticated: value.is_authenticated,
    username: value.username,
    roles: value.roles,
    district_scope: value.district_scope,
    actors: value.actors,
    can_verify: value.can_verify,
    can_manage_campaigns: value.can_manage_campaigns,
  };
}

/**
 * `params` — срез разговора (`as_of`, `include_demo`), `viewParams` — он же
 * плюс оси вида (сезон, массив, хозяйство). Различие не косметика: `/auth/me`
 * осей вида не читает, и послать их туда значило бы сделать вид, что вход
 * сузили выборкой.
 */
export function createApiSource(
  params: QueryParams,
  viewParams: QueryParams = params,
  routePath: string | null = null,
): DataSource {
  return {
    kind: 'api',

    async read<T>(name: string): Promise<ReadResult<T>> {
      for (const bound of ROUTE_ADAPTERS) {
        if (routePath === null || !bound.routes.includes(routePath)) continue;
        const started = bound.read(name, viewParams);
        if (started === null) continue;
        try {
          return { ok: true, value: (await started) as T };
        } catch (cause) {
          return { ok: false, reason: refusal(cause, setNotBuilt(name)) };
        }
      }

      const adapter = ADAPTERS.find((row) => row.match(name));
      if (adapter) {
        try {
          return { ok: true, value: (await adapter.load(name, params)) as T };
        } catch (cause) {
          // Отказ сервера — состояние экрана, а не авария приложения: причина
          // доезжает до человека словами, и экран остаётся проходимым.
          return { ok: false, reason: refusal(cause, setNotBuilt(name)) };
        }
      }
      for (const reader of READERS) {
        const started = reader(name, params);
        if (started) return (await started) as ReadResult<T>;
      }
      // Имя набора в текст отказа не идёт: его читает руководитель, а не
      // разработчик, и «extra-works-2026:COTTON.json» ему ничего не говорит.
      return { ok: false, reason: notConnected() };
    },

    async principal(): Promise<SourcePrincipal | null> {
      return toSourcePrincipal(await api.me(params));
    },
  };
}

/** Вход: CSRF берётся первым — без cookie у формы токена ещё нет. */
export async function signIn(
  username: string,
  password: string,
  params: QueryParams,
): Promise<PrincipalOut> {
  await api.csrf();
  return api.login(username, password, params);
}

export async function signOut(params: QueryParams): Promise<PrincipalOut> {
  await api.csrf();
  return api.logout(params);
}
